// /admin/leads — the pre-launch waitlist inbox.
//
// Reads the waitlist leads captured to Brevo via GET /api/admin/leads (a
// server-side proxy — the Brevo key never reaches the browser). Segment tabs +
// search + a table + CSV export, so Richie can eyeball who's signing up during
// the waitlist stage. When Brevo isn't wired yet the page shows a calm "connect
// Brevo" state instead of an error.

import {
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Card,
  Group,
  SegmentedControl,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { AdminShell } from '../../components/admin-shell/AdminShell';
import { adminGet } from './_api';

type LeadType = 'artist' | 'venue' | 'unknown';

type Lead = {
  email: string;
  firstName: string | null;
  lastName: string | null;
  leadType: LeadType;
  city: string | null;
  country: string | null;
  artistName: string | null;
  genre: string | null;
  venueName: string | null;
  venueCapacity: string | null;
  source: string | null;
  createdAt: string | null;
  listIds: number[];
};

type LeadsResponse = {
  configured: boolean;
  leads: Lead[];
  counts: { all: number; artist: number; venue: number };
  truncated: boolean;
  listIds: { artist: number; venue: number };
};

export const Route = createFileRoute('/admin/leads')({
  component: AdminLeadsPage,
});

function formatJoined(input: string | null): string {
  if (input === null) return '—';
  const ms = Date.parse(input);
  if (!Number.isFinite(ms)) return '—';
  return new Date(ms).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function fullName(lead: Lead): string {
  const parts = [lead.firstName, lead.lastName].filter((s): s is string => s !== null && s !== '');
  return parts.length > 0 ? parts.join(' ') : '—';
}

function location(lead: Lead): string {
  const parts = [lead.city, lead.country].filter((s): s is string => s !== null && s !== '');
  return parts.length > 0 ? parts.join(', ') : '—';
}

function detail(lead: Lead): string {
  if (lead.leadType === 'venue') {
    const bits = [lead.venueName, lead.venueCapacity ? `cap. ${lead.venueCapacity}` : null].filter(
      (s): s is string => s !== null && s !== '',
    );
    return bits.join(' · ') || '—';
  }
  const bits = [lead.artistName, lead.genre].filter((s): s is string => s !== null && s !== '');
  return bits.join(' · ') || '—';
}

function toCsv(leads: Lead[]): string {
  const headers = [
    'created_at',
    'lead_type',
    'first_name',
    'last_name',
    'email',
    'city',
    'country',
    'artist_name',
    'genre',
    'venue_name',
    'venue_capacity',
    'source',
  ];
  const escape = (v: string | null): string => {
    const s = v ?? '';
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const rows = leads.map((l) =>
    [
      l.createdAt,
      l.leadType,
      l.firstName,
      l.lastName,
      l.email,
      l.city,
      l.country,
      l.artistName,
      l.genre,
      l.venueName,
      l.venueCapacity,
      l.source,
    ]
      .map(escape)
      .join(','),
  );
  return [headers.join(','), ...rows].join('\n');
}

function AdminLeadsPage() {
  const [segment, setSegment] = useState<'all' | 'artist' | 'venue'>('all');
  const [search, setSearch] = useState('');

  const query = useQuery({
    queryKey: ['admin', 'leads'],
    queryFn: () => adminGet<LeadsResponse>('/api/admin/leads'),
    staleTime: 60_000,
  });

  const data = query.data;
  const leads = data?.leads ?? [];
  const counts = data?.counts ?? { all: 0, artist: 0, venue: 0 };

  const filtered = useMemo(() => {
    const source = data?.leads ?? [];
    const q = search.trim().toLowerCase();
    return source.filter((l) => {
      if (segment !== 'all' && l.leadType !== segment) return false;
      if (q === '') return true;
      const hay = [
        l.email,
        l.firstName,
        l.lastName,
        l.city,
        l.country,
        l.artistName,
        l.genre,
        l.venueName,
      ]
        .filter((s): s is string => s !== null)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [data, segment, search]);

  function downloadCsv() {
    const csv = toCsv(filtered);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wwaave-leads-${segment}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <AdminShell>
      <Stack gap="md">
        <div>
          <Title order={2}>Waitlist leads</Title>
          <Text c="dimmed" size="sm" maw={720} mt={6}>
            Everyone who&apos;s joined the waitlist, straight from Brevo. These are captured by the
            homepage and the sign-up page while the platform is in the waitlist stage — they never
            touch our database, so Brevo stays the source of truth.
          </Text>
        </div>

        {query.isError ? (
          <Alert color="red" variant="light" title="Couldn't load leads">
            <Stack gap="xs" align="flex-start">
              <Text size="sm">
                {query.error instanceof Error
                  ? query.error.message
                  : 'The leads endpoint did not respond.'}
              </Text>
              <Button size="xs" variant="default" onClick={() => void query.refetch()}>
                Retry
              </Button>
            </Stack>
          </Alert>
        ) : null}

        {query.data && !query.data.configured ? (
          <Alert color="yellow" variant="light" title="Brevo isn't connected yet">
            <Text size="sm">
              Set the <strong>BREVO_API_KEY</strong> secret on the worker to start seeing waitlist
              leads here. Until then the waitlist form returns a friendly &quot;temporarily
              unavailable&quot; message to visitors.
            </Text>
          </Alert>
        ) : null}

        {query.data?.truncated ? (
          <Alert color="blue" variant="light">
            <Text size="sm">
              Showing the most recent leads only — the list is larger than this view fetches.
              Export to CSV or check Brevo directly for the full set.
            </Text>
          </Alert>
        ) : null}

        <Group justify="space-between" align="flex-end" wrap="wrap" gap="sm">
          <SegmentedControl
            value={segment}
            onChange={(v) => setSegment(v === 'artist' || v === 'venue' ? v : 'all')}
            data={[
              { label: `All (${counts.all})`, value: 'all' },
              { label: `Artists (${counts.artist})`, value: 'artist' },
              { label: `Venues (${counts.venue})`, value: 'venue' },
            ]}
          />
          <Group gap="sm" wrap="wrap">
            <TextInput
              placeholder="Search name, email, city…"
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              style={{ minWidth: 240 }}
            />
            <Button
              variant="default"
              onClick={downloadCsv}
              disabled={filtered.length === 0}
            >
              Export CSV
            </Button>
          </Group>
        </Group>

        <Card withBorder padding={0} radius="md">
          {query.isLoading ? (
            <Text c="dimmed" size="sm" p="lg">
              Loading leads…
            </Text>
          ) : filtered.length === 0 ? (
            <Text c="dimmed" size="sm" p="lg">
              {leads.length === 0
                ? 'No waitlist leads yet. They’ll appear here as people sign up.'
                : 'No leads match this filter.'}
            </Text>
          ) : (
            <Table.ScrollContainer minWidth={720}>
              <Table striped highlightOnHover verticalSpacing="sm" horizontalSpacing="md">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Email</Table.Th>
                    <Table.Th>Type</Table.Th>
                    <Table.Th>Location</Table.Th>
                    <Table.Th>Detail</Table.Th>
                    <Table.Th>Joined</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {filtered.map((lead) => (
                    <Table.Tr key={`${lead.email}-${lead.createdAt ?? ''}`}>
                      <Table.Td>{fullName(lead)}</Table.Td>
                      <Table.Td>
                        <Anchor href={`mailto:${lead.email}`} size="sm">
                          {lead.email}
                        </Anchor>
                      </Table.Td>
                      <Table.Td>
                        <Badge
                          variant="light"
                          color={
                            lead.leadType === 'venue'
                              ? 'tide'
                              : lead.leadType === 'artist'
                                ? 'brand'
                                : 'gray'
                          }
                        >
                          {lead.leadType}
                        </Badge>
                      </Table.Td>
                      <Table.Td>{location(lead)}</Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {detail(lead)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {formatJoined(lead.createdAt)}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </Card>

        <Box>
          <Text size="xs" c="dimmed">
            Showing {filtered.length} of {counts.all} total.
          </Text>
        </Box>
      </Stack>
    </AdminShell>
  );
}
